import type { ProjectTreeDto } from '@fyr/shared';

export type NodeKind =
  | 'workspace'
  /** The personal Dashboard (across all my projects) — a link out of the project, below My Workspace. */
  | 'myDashboard'
  | 'root'
  | 'folder'
  | 'sheet'
  | 'doc'
  | 'plans'
  | 'plan'
  | 'governance'
  | 'board'
  | 'reporting'
  | 'execDashboard'
  | 'dashboard'
  | 'report'
  | 'learning'
  /** §34 M24 the person's own Mail (not a project's). */
  | 'mail'
  /** §36 M25 the Fyr Browser (the desktop app; the web shows how to get it). */
  | 'browser'
  | 'workflows'
  | 'actions'
  | 'admin'
  | 'people'
  | 'chat'
  /** M18 a connected folder (SharePoint / OneDrive / a local folder), browsed on its own page. */
  | 'connected';

export interface TreeNode {
  key: string; // unique, e.g. "folder:<id>"
  kind: NodeKind;
  id: string | null; // entity id (folder/grid/document/plan)
  label: string;
  href: string | null;
  children: TreeNode[];
  isPrivate?: boolean;
  eyesOnly?: boolean;
  sub?: string;
  /** Documents (§15): docx | xlsx | pdf | image | native — picks the icon. */
  format?: string; // secondary text, e.g. doc version/status
  /** §19 M9.3: an unread count shown as a pill (Chat). */
  count?: number;
  /** M18 a connected folder that needs attention (paused / error / disconnected). */
  attention?: boolean;
  /** M18.4 a restricted folder: `locked` = this person can't enter it; `restricted` = they can (it's limited). */
  folderAccess?: 'locked' | 'restricted';
}

/** Shape the flat API payload into Explorer's tree: My Workspace, the project library, and Plans. */
export function buildTree(
  t: ProjectTreeDto,
  projectName: string,
  me: string,
  labels: {
    workspace: string;
    /** The personal Dashboard's label. */
    myDashboard: string;
    plans: string;
    master: string;
    myPlan: string;
    governance: string;
    workflows: string;
    /** §27 M13.2 the project's actions (board / list). */
    actions: string;
    /** §6.3/§6.4 — shown to roles that may read reports (not suppliers: reports include finance). */
    /** `exec` null: the Executive dashboard flag is off. */
    reporting: { section: string; exec: string | null; dashboard: string; report: string } | null;
    /** §23 M12.7 Learning, a workspace section of its own (when the organisation has it switched on). */
    learning: string | null;
    /** §34 M24 Mail (when switched on for the person). */
    mail?: string | null;
    /** §36 M25 the Fyr Browser (when switched on for the person; not in the phone apps). */
    browser?: string | null;
    /** §19 M9.3 chat: its label and how many unread messages. */
    chat: { label: string; unread: number } | null;
    /** Shown only to tenant / platform admins. */
    admin: { section: string; people: string } | null;
  },
): TreeNode[] {
  const base = `/p/${t.projectId}`;
  const sheetNode = (s: ProjectTreeDto['sheets'][number]): TreeNode => ({
    key: `sheet:${s.id}`,
    kind: 'sheet',
    id: s.id,
    label: s.name,
    href: `${base}/g/${s.id}`,
    children: [],
    isPrivate: s.visibility === 'private',
  });
  const docNode = (d: ProjectTreeDto['documents'][number]): TreeNode => ({
    key: `doc:${d.id}`,
    kind: 'doc',
    id: d.id,
    label: d.title,
    href: `${base}/d/${d.id}`,
    children: [],
    eyesOnly: d.aclMode === 'eyes_only',
    isPrivate: d.visibility === 'private',
    sub: `v${d.version}`,
    format: d.format,
  });
  const connectedNode = (c: ProjectTreeDto['connected'][number]): TreeNode => ({
    key: `connected:${c.id}`,
    kind: 'connected',
    id: c.id,
    label: c.name,
    href: `${base}/cf/${c.id}`,
    children: [],
    attention: c.status !== 'active',
  });
  const connectedIn = (folderId: string | null) =>
    (t.connected ?? []).filter((c) => c.folderId === folderId).map(connectedNode);
  const folderNode = (f: ProjectTreeDto['folders'][number]): TreeNode => ({
    key: `folder:${f.id}`,
    kind: 'folder',
    id: f.id,
    label: f.name,
    href: `${base}/f/${f.id}`,
    folderAccess: f.locked ? 'locked' : f.access === 'restricted' ? 'restricted' : undefined,
    children: [
      ...t.folders
        .filter((c) => c.parentId === f.id)
        .sort(byPos)
        .map(folderNode),
      ...t.sheets.filter((s) => s.folderId === f.id && s.visibility === 'project').map(sheetNode),
      ...t.documents.filter((d) => d.folderId === f.id && d.visibility === 'project').map(docNode),
      ...connectedIn(f.id),
    ],
  });
  const byPos = (a: { position: number; name: string }, b: { position: number; name: string }) =>
    a.position - b.position || a.name.localeCompare(b.name);

  const plans = [...t.plans].sort(
    (a, b) => rank(a) - rank(b) || (a.owner?.name ?? '').localeCompare(b.owner?.name ?? ''),
  );
  function rank(p: ProjectTreeDto['plans'][number]) {
    return p.kind === 'master' ? 0 : p.owner?.id === me ? 1 : p.kind === 'workstream' ? 2 : 3;
  }

  const nodes: TreeNode[] = [
    {
      key: 'workspace',
      kind: 'workspace',
      id: null,
      label: labels.workspace,
      href: `${base}/workspace`,
      children: [
        ...t.sheets.filter((s) => s.visibility === 'private' && s.ownerId === me).map(sheetNode),
        ...t.documents.filter((d) => d.visibility === 'private' && d.owner?.id === me).map(docNode),
      ],
    },
    {
      key: 'myDashboard',
      kind: 'myDashboard',
      id: null,
      label: labels.myDashboard,
      href: '/dashboard',
      children: [],
    },
    ...(labels.mail
      ? [{ key: 'mail', kind: 'mail' as const, id: null, label: labels.mail, href: '/mail', children: [] }]
      : []),
    ...(labels.browser
      ? [
          {
            key: 'browser',
            kind: 'browser' as const,
            id: null,
            label: labels.browser,
            href: '/browser',
            children: [],
          },
        ]
      : []),
    ...(labels.learning
      ? [
          {
            key: 'learning',
            kind: 'learning' as const,
            id: null,
            label: labels.learning,
            href: '/learning',
            children: [],
          },
        ]
      : []),
    {
      key: 'root',
      kind: 'root',
      id: null,
      label: projectName,
      href: `${base}/f/root`,
      children: [
        ...t.folders
          .filter((f) => !f.parentId)
          .sort(byPos)
          .map(folderNode),
        ...t.sheets.filter((s) => !s.folderId && s.visibility === 'project').map(sheetNode),
        ...t.documents.filter((d) => !d.folderId && d.visibility === 'project').map(docNode),
        ...connectedIn(null),
      ],
    },
    {
      key: 'plans',
      kind: 'plans',
      id: null,
      label: labels.plans,
      href: null,
      // §27 M13.1(c) plans nest under the plan they roll up to (mini / workstream → master)
      children: (() => {
        const shown = new Set(plans.map((p) => p.id));
        const node = (p: (typeof plans)[number]): TreeNode => ({
          key: `plan:${p.id}`,
          kind: 'plan' as const,
          id: p.id,
          label:
            p.kind === 'master'
              ? labels.master
              : p.owner?.id === me
                ? labels.myPlan
                : (p.owner?.name ?? p.name),
          href: `${base}/plan/${p.id}`,
          children: plans.filter((c) => c.parentPlanId === p.id).map(node),
        });
        return plans.filter((p) => !p.parentPlanId || !shown.has(p.parentPlanId)).map(node);
      })(),
    },
    // §23 M12.3 chat can be switched off for the organisation.
    ...(labels.chat
      ? [
          {
            key: 'chat',
            kind: 'chat' as const,
            id: null,
            label: labels.chat.label,
            href: `${base}/chat`,
            children: [],
            count: labels.chat.unread,
          },
        ]
      : []),
    {
      key: 'governance',
      kind: 'governance',
      id: null,
      label: labels.governance,
      href: null,
      children: [
        ...t.boards.map((b) => ({
          key: `board:${b.id}`,
          kind: 'board' as const,
          id: b.id,
          label: b.name,
          href: `${base}/board/${b.id}`,
          children: [],
        })),
        {
          key: 'actions',
          kind: 'actions' as const,
          id: null,
          label: labels.actions,
          href: `${base}/actions`,
          children: [],
        },
        {
          key: 'workflows',
          kind: 'workflows' as const,
          id: null,
          label: labels.workflows,
          href: `${base}/workflows`,
          children: [],
        },
      ],
    },
    ...(labels.reporting
      ? [
          {
            key: 'reporting',
            kind: 'reporting' as const,
            id: null,
            label: labels.reporting.section,
            href: null,
            children: [
              ...(labels.reporting.exec
                ? [
                    {
                      key: 'execDashboard',
                      kind: 'execDashboard' as const,
                      label: labels.reporting.exec,
                      href: `${base}/dashboard/exec`,
                    },
                  ]
                : []),
              {
                key: 'dashboard',
                kind: 'dashboard' as const,
                label: labels.reporting.dashboard,
                href: `${base}/dashboard`,
              },
              {
                key: 'report',
                kind: 'report' as const,
                label: labels.reporting.report,
                href: `${base}/report`,
              },
            ].map((n) => ({ ...n, id: null, children: [] })),
          },
        ]
      : []),
    ...(labels.admin
      ? [
          {
            key: 'admin',
            kind: 'admin' as const,
            id: null,
            label: labels.admin.section,
            href: null,
            children: [
              {
                key: 'people',
                kind: 'people' as const,
                id: null,
                label: labels.admin.people,
                href: '/admin',
                children: [],
              },
            ],
          },
        ]
      : []),
  ];
  // #142 M19.2 someone who isn't on the project sees only what's shared with them: no plans, chat, governance
  return t.guest ? nodes.filter((n) => n.kind === 'learning' || n.kind === 'root') : nodes;
}

/** Path from a root to the node with `key` (for breadcrumbs and auto-expansion). */
export function pathTo(nodes: TreeNode[], key: string): TreeNode[] | null {
  for (const n of nodes) {
    if (n.key === key) return [n];
    const sub = pathTo(n.children, key);
    if (sub) return [n, ...sub];
  }
  return null;
}

export function isDescendant(nodes: TreeNode[], ancestorKey: string, key: string): boolean {
  const path = pathTo(nodes, key);
  return !!path?.some((n) => n.key === ancestorKey);
}
